import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { theme } from '../../styles/theme';
import { useCalendlyPopup } from '../../hooks/useCalendlyPopup';
import { DeepDiveModal } from '../DeepDiveModal';
import { QuickAnswerModal } from '../QuickAnswerModal';
import { PhoneModal } from '../PhoneModal';
import { Breadcrumb } from '../Breadcrumb';

const Header = styled.header<{ $scrolled: boolean }>`
  background: ${props => props.$scrolled ? 'rgba(35, 43, 59, 0.98)' : '#232B3B'};
  padding: ${props => props.$scrolled ? '8px 0' : '12px 0'};
  position: fixed !important;
  top: 0 !important;
  width: 100% !important;
  z-index: 2147483646 !important;
  box-shadow: ${props => props.$scrolled ? '0 4px 20px rgba(0, 0, 0, 0.25)' : '0 2px 10px rgba(0, 0, 0, 0.15)'};
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${props => props.$scrolled ? '8px 0' : '12px 0'};
  }
`;

const HeaderContainer = styled.div<{ $scrolled: boolean }>`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
  display: grid;
  grid-template-columns: 250px 1fr 400px;
  align-items: center;
  gap: 30px;
  position: relative;
  height: ${props => props.$scrolled ? '52px' : '64px'};
  transition: height 0.3s ease;
  
  @media (max-width: 1024px) {
    padding: 0 30px;
  }
  
  @media (max-width: 900px) {
    padding: 0 20px;
    height: ${props => props.$scrolled ? '48px' : '56px'};
    display: flex;
    grid-template-columns: none;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0 15px;
    height: ${props => props.$scrolled ? '48px' : '56px'};
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  position: relative;
  z-index: 1000000;
  
  @media (max-width: 900px) {
    position: relative;
    left: 0;
  }
`;

const DesktopLogo = styled(Logo)`
  position: relative;
  left: 0;
  @media (max-width: 900px) {
    display: none;
  }
`;

const LogoImg = styled.img`
  height: 36px;
  width: auto;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    height: 32px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LogoTagline = styled.span`
  color: #ffed4a;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  opacity: 0.85;
  font-style: italic;
`;

const Nav = styled.nav`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 1100px) {
    gap: 8px;
  }
  
  @media (max-width: 900px) {
    display: none;
  }
`;


const NavLink = styled(Link)`
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  padding: 8px 14px;
  position: relative;
  border-radius: 6px;
  white-space: nowrap;
  
  @media (max-width: 1100px) {
    font-size: 13px;
    padding: 6px 10px;
  }
  
  &:hover {
    background: rgba(255, 215, 0, 0.1);
    color: #ffd700;
    transform: translateY(-1px);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 0;
    height: 2px;
    background: #ffd700;
    transition: all 0.3s;
    transform: translateX(-50%);
  }
  
  &:hover::after {
    width: 80%;
  }
`;

const ToolsDropdown = styled.div`
  position: relative;
  display: inline-block;
`;

const ToolsDropdownButton = styled.button`
  color: rgba(255, 255, 255, 0.9);
  background: transparent;
  border: none;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  padding: 8px 14px;
  position: relative;
  border-radius: 6px;
  white-space: nowrap;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  
  @media (max-width: 1100px) {
    font-size: 13px;
    padding: 6px 10px;
  }
  
  &:hover {
    background: rgba(255, 215, 0, 0.1);
    color: #ffd700;
    transform: translateY(-1px);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 0;
    height: 2px;
    background: #ffd700;
    transition: all 0.3s;
    transform: translateX(-50%);
  }
  
  &:hover::after {
    width: 80%;
  }
  
  &.open {
    background: rgba(255, 215, 0, 0.1);
    color: #ffd700;
  }
`;

const ToolsDropdownContent = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: #2d3748;
  border: 1px solid rgba(255, 215, 0, 0.3);
  border-radius: 8px;
  padding: 8px;
  min-width: 220px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
  z-index: 1002;
  opacity: 0;
  visibility: hidden;
  transform: translateX(-50%) translateY(-10px);
  transition: all 0.2s;
  
  &.open {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(0);
  }
`;

const ToolsDropdownItem = styled.button`
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  color: white;
  border: 1px solid transparent;
  padding: 12px 16px;
  margin-bottom: 8px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &:hover {
    background: rgba(255, 215, 0, 0.15);
    border-color: rgba(255, 215, 0, 0.5);
    color: #ffd700;
    transform: translateX(2px);
  }
`;

const PhoneWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const TalkToCap = styled.span`
  color: white;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const PhoneNumber = styled.a`
  color: white;
  text-decoration: none;
  font-size: 16px;
  font-weight: bold;
  transition: all 0.3s;
  
  &:hover {
    color: #ffd700;
    text-decoration: underline;
  }
`;

const ContactDropdown = styled.div`
  position: relative;
  display: inline-block;
`;

const ContactMainButton = styled.button`
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 215, 0, 0.05) 100%);
  color: white;
  border: 1px solid rgba(255, 215, 0, 0.5);
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  white-space: nowrap;
  
  @media (max-width: 1100px) {
    padding: 6px 10px;
    font-size: 12px;
  }
  
  &:hover {
    background: rgba(255, 215, 0, 0.15);
    border-color: #FFD700;
  }
  
  .arrow {
    font-size: 10px;
    transition: transform 0.2s;
  }
  
  &.open {
    background: rgba(255, 215, 0, 0.2);
    border-color: #FFD700;
    
    .arrow {
      transform: rotate(180deg);
    }
  }
`;

const DropdownContent = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #2d3748;
  border: 1px solid rgba(255, 215, 0, 0.3);
  border-radius: 8px;
  padding: 16px;
  min-width: 340px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
  z-index: 1002;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: all 0.2s;
  
  &.open {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    right: -20px;
    min-width: calc(100vw - 40px);
  }
`;

const ContactOption = styled.div`
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 12px;
  background: rgba(255, 255, 255, 0.05);
  
  &:last-child {
    margin-bottom: 0;
  }
  
  h4 {
    color: #FFD700;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  
  .subtitle {
    font-size: 11px;
    color: #a0a0a0;
    margin-bottom: 10px;
  }
`;

const ContactButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
  flex-wrap: wrap;
`;

const ContactMethodButton = styled.a`
  background: linear-gradient(135deg, #FFD700 0%, #ffed4e 100%);
  color: #232B3B;
  padding: 6px 14px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 215, 0, 0.3);
  }
`;

const Disclaimer = styled.p`
  font-size: 11px;
  color: #ffed4a;
  margin-top: 10px;
  padding: 8px;
  background: rgba(255, 215, 0, 0.1);
  border-radius: 4px;
  line-height: 1.4;
  font-style: italic;
`;

const RightSection = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: flex-end;
  
  @media (max-width: 1100px) {
    gap: 12px;
  }
  
  @media (max-width: 900px) {
    display: none;
  }
`;

const HeaderContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: white;
  
  .icon {
    font-size: 14px;
    color: #ffd700;
  }
  
  a {
    color: white;
    text-decoration: none;
    font-weight: 500;
    font-size: 14px;
    transition: color 0.3s;
    
    &:hover {
      color: #ffd700;
    }
  }
  
  span {
    font-size: 14px;
    font-weight: 500;
  }
`;

const CallNowSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  border-radius: 6px;
  margin-right: 12px;
`;

const CallNowText = styled.span`
  color: #232B3B;
  font-weight: 800;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CallNowNumber = styled.a`
  color: #232B3B;
  text-decoration: none;
  font-weight: 800;
  font-size: 16px;
  transition: all 0.3s;
  
  &:hover {
    text-decoration: underline;
  }
`;

const GetInTouchButton = styled.button`
  background: transparent;
  color: white;
  border: 1px solid white;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s;
  border-radius: 0;
  
  &:hover {
    background: white;
    color: #232B3B;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 255, 255, 0.2);
  }
  
  &:active {
    transform: translateY(0);
  }
`;



const CTAButton = styled(Link)`
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  color: #232B3B;
  padding: 10px 24px;
  border-radius: 6px;
  font-weight: bold;
  font-size: 14px;
  text-decoration: none;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(255, 215, 0, 0.3);
  }
`;

const MobileMenuButton = styled.button<{ $isOpen: boolean }>`
  display: none;
  background: transparent;
  border: 1px solid #87CEEB;
  border-radius: 0;
  color: #87CEEB;
  cursor: pointer;
  padding: 8px;
  z-index: 2147483648 !important;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-left: auto;
  position: relative;
  width: auto;
  height: 40px;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  
  &:hover {
    background: rgba(135, 206, 235, 0.1);
  }
  
  @media (max-width: 900px) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 8px 12px;
  }
`;

const MenuText = styled.span`
  color: #87CEEB;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
`;

const HamburgerIcon = styled.div<{ $isOpen: boolean }>`
  width: 20px;
  height: 18px;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  span {
    display: block;
    position: absolute;
    height: 1px;
    width: 100%;
    background: #87CEEB;
    transition: all 0.25s ease;
    
    &:nth-child(1) {
      top: ${props => props.$isOpen ? '50%' : '0'};
      transform: ${props => props.$isOpen ? 'translateY(-50%) rotate(45deg)' : 'none'};
    }
    
    &:nth-child(2) {
      top: 50%;
      transform: translateY(-50%);
      opacity: ${props => props.$isOpen ? 0 : 1};
      width: ${props => props.$isOpen ? '0' : '100%'};
    }
    
    &:nth-child(3) {
      bottom: ${props => props.$isOpen ? '50%' : '0'};
      transform: ${props => props.$isOpen ? 'translateY(50%) rotate(-45deg)' : 'none'};
    }
  }
`;

const MobileMenuOverlay = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'block' : 'none'} !important;
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  background: rgba(0, 0, 0, 0.8) !important;
  z-index: 2147483645 !important;
  
  @media (min-width: 901px) {
    display: none !important;
  }
`;

const MobileMenu = styled.div<{ $isOpen: boolean }>`
  position: fixed !important;
  top: 0 !important;
  right: 0 !important;
  width: 250px !important;
  height: 100vh !important;
  padding-top: 0 !important;
  background: white !important;
  border-left: 1px solid #87CEEB !important;
  z-index: 2147483647 !important;
  overflow: hidden;
  transform: ${props => props.$isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s ease;
  display: block !important;
  
  @media (min-width: 901px) {
    display: none !important;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding-top: 0 !important;
    width: 200px !important;
  }
`;

const MobileNavContainer = styled.div`
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 0;
  height: calc(100vh - 50px);
  overflow-y: auto;
  max-width: 100%;
  margin: 0;
  margin-top: 50px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 12px;
  }
`;

const MobileNavLink = styled(Link)`
  color: #333;
  text-decoration: none;
  font-size: 15px;
  font-weight: 400;
  padding: 10px 0;
  border-bottom: 1px solid #e0e0e0;
  transition: all 0.2s ease;
  display: block;
  
  &:hover {
    color: #87CEEB;
    padding-left: 10px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 14px;
    padding: 8px 0;
  }
`;

const MobilePhoneNumber = styled.a`
  color: #ffd700;
  text-decoration: none;
  font-size: 20px;
  font-weight: bold;
  padding: 15px;
  text-align: center;
  display: block;
  border: 2px solid #ffd700;
  border-radius: 8px;
  margin: 20px 0;
  
  &:active {
    background: rgba(255, 215, 0, 0.1);
  }
`;

const MobileCTAButton = styled(Link)`
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  color: #232B3B;
  padding: 18px;
  border-radius: 8px;
  font-weight: bold;
  font-size: 18px;
  text-decoration: none;
  text-align: center;
  display: block;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 5px 15px rgba(255, 215, 0, 0.3);
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 7px 20px rgba(255, 215, 0, 0.4);
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const MobileHeader = styled.div`
  display: none;
  
  @media (max-width: 900px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    gap: 15px;
    padding: 0;
  }
`;

const DesktopSection = styled.div`
  display: contents;
  
  @media (max-width: 900px) {
    display: none;
  }
`;

const Footer = styled.footer`
  background: #2d3748;
  padding: 50px 20px 30px;
  color: white;
`;

const MobileFixedFooter = styled.div<{ $isVisible: boolean }>`
  display: none;
  
  @media (max-width: 900px) {
    display: flex;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: 60px;
    background: linear-gradient(135deg, #232B3B 0%, #2c3e50 100%);
    border-top: 2px solid rgba(255, 215, 0, 0.3);
    z-index: 9997;
    align-items: center;
    justify-content: space-between;
    padding: 0 15px;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
    transform: ${props => props.$isVisible ? 'translateY(0)' : 'translateY(100%)'};
    transition: transform 0.3s ease-in-out;
  }
`;

const TalkWithCapButton = styled.button`
  background: linear-gradient(135deg, #ffd700 0%, #ffa500 100%);
  color: #232B3B;
  padding: 10px 20px;
  border-radius: 0;
  border: 1px solid #232B3B;
  font-weight: 800;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 10px rgba(255, 215, 0, 0.3);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.3);
    transform: translate(-50%, -50%);
    transition: width 0.5s, height 0.5s;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 30px rgba(255, 215, 0, 0.5);
    
    &::before {
      width: 300px;
      height: 300px;
    }
  }
  
  &:active {
    transform: translateY(0);
  }
  
  .icon {
    font-size: 18px;
  }
`;

const FooterMenuButton = styled.button`
  display: flex;
  background: transparent;
  border: 1px solid #87CEEB;
  border-radius: 0;
  cursor: pointer;
  padding: 8px;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(135, 206, 235, 0.1);
  }
`;

const FooterContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
  gap: 40px;
  align-items: center;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 30px;
  }
`;

const FooterLogo = styled.img`
  height: 60px;
  width: auto;
`;

const FooterCenter = styled.div`
  text-align: center;
`;

const FooterDescription = styled.p`
  font-size: 14px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 15px;
`;

const FooterTagline = styled.p`
  font-size: 16px;
  font-weight: bold;
  color: white;
  
  strong {
    color: #ffd700;
  }
`;

const FooterBBBContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    align-items: center;
  }
`;

const FooterBBBText = styled.p`
  font-size: 14px;
  font-weight: bold;
  color: white;
  margin-bottom: 10px;
  text-align: center;
`;

const FooterBBBLink = styled.a`
  text-decoration: none;
  display: block;
`;

const FooterBBB = styled.img`
  height: 60px;
  width: auto;
  display: block;
`;

const Copyright = styled.div`
  text-align: center;
  padding: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 30px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
`;


const FooterLink = styled(Link)`
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-size: 14px;
  transition: all 0.3s;
  
  &:hover {
    color: #ffd700;
    text-decoration: underline;
  }
`;

const FooterAnchor = styled.a`
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-size: 14px;
  transition: all 0.3s;
  cursor: pointer;
  
  &:hover {
    color: #ffd700;
    text-decoration: underline;
  }
`;

const FooterLinksSection = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 20px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const FooterLinksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 30px;
  margin-bottom: 30px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FooterColumnTitle = styled.h4`
  color: #ffd700;
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #ffffff;
`;

const MainContent = styled.main`
  padding-top: 76px;
  min-height: 100vh;
  
  @media (max-width: 900px) {
    padding-top: 68px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding-top: 68px;
    padding-bottom: 70px;
  }
`;

interface SharedLayoutProps {
  children: React.ReactNode;
}

export const SharedLayout: React.FC<SharedLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openCalendly } = useCalendlyPopup();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isContactDropdownOpen, setIsContactDropdownOpen] = useState(false);
  const [isScheduleDropdownOpen, setIsScheduleDropdownOpen] = useState(false);
  const [isToolsDropdownOpen, setIsToolsDropdownOpen] = useState(false);
  const [isDeepDiveModalOpen, setIsDeepDiveModalOpen] = useState(false);
  const [isQuickAnswerModalOpen, setIsQuickAnswerModalOpen] = useState(false);
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showStickyFooter, setShowStickyFooter] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(isScrolled);
      
      // Show sticky footer after scrolling past hero section
      setShowStickyFooter(window.scrollY > 600);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Handle mobile menu state and body scroll lock
  useEffect(() => {
    // Clean up body styles on unmount or when menu closes
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, []);
  
  // Close menu on route change
  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname]);
  
  // Bot protection - obfuscated contact info
  const getCapPhone = () => {
    const a = '419';
    const b = '309';
    const c = '7797';
    return `${a}${b}${c}`;
  };
  
  const getCapEmail = () => {
    const user = 'cap';
    const domain = 'capassociates';
    const ext = 'net';
    return `${user}@${domain}.${ext}`;
  };
  
  const toggleMobileMenu = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Improved toggle with body scroll lock
    const newState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newState);
    
    // Prevent body scroll when menu is open
    if (newState) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
  };
  
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    // Reset body styles
    const scrollY = document.body.style.top;
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
    document.body.style.top = '';
    if (scrollY) {
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
  };
  
  const toggleContactDropdown = () => {
    setIsContactDropdownOpen(!isContactDropdownOpen);
  };
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isDropdownClick = target.closest('.dropdown-container');
      
      if (!isDropdownClick) {
        setIsContactDropdownOpen(false);
        setIsScheduleDropdownOpen(false);
        setIsToolsDropdownOpen(false);
      }
    };
    
    if (isContactDropdownOpen || isScheduleDropdownOpen || isToolsDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isContactDropdownOpen, isScheduleDropdownOpen, isToolsDropdownOpen]);
  
  const handleQuintessentialClick = (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (location.pathname === '/') {
      // Already on home page, just scroll
      const element = document.getElementById('quintessential-question');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to home page first, then scroll
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById('quintessential-question');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };
  
  return (
    <PageWrapper>
      <Header $scrolled={scrolled}>
        <HeaderContainer $scrolled={scrolled}>
          <MobileHeader>
            <Logo to="/" onClick={closeMobileMenu} style={{ flexShrink: 0 }}>
              <LogoContainer>
                <LogoImg src="/logo.svg" alt="Annuity Answers" />
                <LogoTagline>"Keep it real"</LogoTagline>
              </LogoContainer>
            </Logo>
            <MobileMenuButton 
              $isOpen={isMobileMenuOpen}
              onClick={(e) => toggleMobileMenu(e)}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-navigation"
              type="button"
            >
              <HamburgerIcon $isOpen={isMobileMenuOpen}>
                <span></span>
                <span></span>
                <span></span>
              </HamburgerIcon>
              <MenuText>MENU</MenuText>
            </MobileMenuButton>
          </MobileHeader>
          
          <MobileMenuOverlay $isOpen={isMobileMenuOpen} onClick={closeMobileMenu} />
          
          <DesktopSection>
            <DesktopLogo to="/">
              <LogoContainer>
                <LogoImg src="/logo.svg" alt="Annuity Answers" />
                <LogoTagline>"Keep it real"</LogoTagline>
              </LogoContainer>
            </DesktopLogo>
            
            <Nav>
              <NavLink to="/">Home</NavLink>
              
              <ToolsDropdown className="dropdown-container">
                <ToolsDropdownButton
                  className={isScheduleDropdownOpen ? 'open' : ''}
                  onClick={() => setIsScheduleDropdownOpen(!isScheduleDropdownOpen)}
                >
                  About
                  <span style={{ fontSize: '10px' }}>▼</span>
                </ToolsDropdownButton>
                <ToolsDropdownContent className={isScheduleDropdownOpen ? 'open' : ''}>
                  <ToolsDropdownItem
                    onClick={() => {
                      navigate('/about-us');
                      setIsScheduleDropdownOpen(false);
                    }}
                  >
                    ℹ️ About Us
                  </ToolsDropdownItem>
                  <ToolsDropdownItem
                    onClick={() => {
                      navigate('/annuity-basics');
                      setIsScheduleDropdownOpen(false);
                    }}
                  >
                    📚 Learn
                  </ToolsDropdownItem>
                  <ToolsDropdownItem
                    onClick={(e) => {
                      e.preventDefault();
                      setIsScheduleDropdownOpen(false);
                      const element = document.getElementById('testimonials-section');
                      if (element) element.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    ⭐ Testimonials
                  </ToolsDropdownItem>
                </ToolsDropdownContent>
              </ToolsDropdown>
              
              <ToolsDropdown className="dropdown-container">
                <ToolsDropdownButton
                  className={isToolsDropdownOpen ? 'open' : ''}
                  onClick={() => setIsToolsDropdownOpen(!isToolsDropdownOpen)}
                >
                  Tools
                  <span style={{ fontSize: '10px' }}>▼</span>
                </ToolsDropdownButton>
                <ToolsDropdownContent className={isToolsDropdownOpen ? 'open' : ''}>
                  <ToolsDropdownItem
                    onClick={() => {
                      setIsQuickAnswerModalOpen(true);
                      setIsToolsDropdownOpen(false);
                    }}
                  >
                    ⚡ Quick Answer Tool
                  </ToolsDropdownItem>
                  <ToolsDropdownItem
                    onClick={() => {
                      setIsDeepDiveModalOpen(true);
                      setIsToolsDropdownOpen(false);
                    }}
                  >
                    🔍 Deep Dive Analysis
                  </ToolsDropdownItem>
                </ToolsDropdownContent>
              </ToolsDropdown>
              
              <ToolsDropdown className="dropdown-container">
                <ToolsDropdownButton
                  className={isContactDropdownOpen ? 'open' : ''}
                  onClick={() => setIsContactDropdownOpen(!isContactDropdownOpen)}
                >
                  Contact
                  <span style={{ fontSize: '10px' }}>▼</span>
                </ToolsDropdownButton>
                <ToolsDropdownContent className={isContactDropdownOpen ? 'open' : ''}>
                  <ToolsDropdownItem
                    onClick={() => {
                      navigate('/contact');
                      setIsContactDropdownOpen(false);
                    }}
                  >
                    📧 Contact Page
                  </ToolsDropdownItem>
                  <div style={{ 
                    borderTop: '1px solid rgba(255, 215, 0, 0.2)', 
                    marginTop: '8px', 
                    paddingTop: '8px' 
                  }}>
                    <div style={{ 
                      color: '#ffd700', 
                      fontSize: '12px', 
                      fontWeight: '600', 
                      padding: '8px 16px 4px 16px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Free Consultation
                    </div>
                    <ToolsDropdownItem
                      onClick={() => {
                        navigate('/schedule');
                        setIsContactDropdownOpen(false);
                      }}
                      style={{ paddingLeft: '28px', fontSize: '13px' }}
                    >
                      👥 For Annuity Owners
                    </ToolsDropdownItem>
                    <ToolsDropdownItem
                      onClick={() => {
                        navigate('/schedule');
                        setIsContactDropdownOpen(false);
                      }}
                      style={{ paddingLeft: '28px', fontSize: '13px' }}
                    >
                      💼 For Advisors
                    </ToolsDropdownItem>
                  </div>
                </ToolsDropdownContent>
              </ToolsDropdown>
              
            </Nav>
          </DesktopSection>
          
          <RightSection>
            <TalkWithCapButton 
              onClick={() => setIsPhoneModalOpen(true)}
              style={{ 
                padding: '8px 16px',
                fontSize: '13px',
                borderRadius: '6px'
              }}
            >
              <span className="icon">📞</span>
              <span>TALK WITH CAP</span>
            </TalkWithCapButton>
          </RightSection>
        </HeaderContainer>
      </Header>
      
      {/* Mobile Menu moved outside Header to ensure proper stacking */}
      <MobileMenu 
        $isOpen={isMobileMenuOpen}
        id="mobile-navigation"
        role="navigation"
        aria-hidden={!isMobileMenuOpen}
      >
        <div style={{
          position: 'sticky',
          top: 0,
          background: '#f8f8f8',
          borderBottom: '2px solid #87CEEB',
          padding: '10px 15px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 10,
          height: '50px'
        }}>
          <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#333' }}>Menu</span>
          <button
            onClick={closeMobileMenu}
            style={{
              background: 'transparent',
              border: '1px solid #87CEEB',
              color: '#87CEEB',
              padding: '5px 12px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              borderRadius: '4px'
            }}
            aria-label="Close menu"
          >
            ✕ CLOSE
          </button>
        </div>
        <MobileNavContainer>
            <MobileNavLink to="/" onClick={closeMobileMenu}>
              Home
            </MobileNavLink>
            
            <div style={{ marginTop: '8px', marginBottom: '8px' }}>
              <div style={{ color: '#87CEEB', fontSize: '11px', fontWeight: '700', marginBottom: '6px', paddingLeft: '0px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                About
              </div>
              <MobileNavLink to="/about-us" onClick={closeMobileMenu} style={{ paddingLeft: '12px', fontSize: '13px', padding: '6px 0 6px 12px' }}>
                About Us
              </MobileNavLink>
              <MobileNavLink to="/annuity-basics" onClick={closeMobileMenu} style={{ paddingLeft: '12px', fontSize: '13px', padding: '6px 0 6px 12px' }}>
                Learn
              </MobileNavLink>
              <MobileNavLink to="#" onClick={(e) => {
                e.preventDefault();
                closeMobileMenu();
                const element = document.getElementById('testimonials-section');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }} style={{ paddingLeft: '12px', fontSize: '13px', padding: '6px 0 6px 12px' }}>
                Testimonials
              </MobileNavLink>
            </div>
            
            <div style={{ marginTop: '8px', marginBottom: '8px' }}>
              <div style={{ color: '#87CEEB', fontSize: '11px', fontWeight: '700', marginBottom: '6px', paddingLeft: '0px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Tools
              </div>
              <MobileNavLink to="#" onClick={(e) => {
                e.preventDefault();
                setIsQuickAnswerModalOpen(true);
                closeMobileMenu();
              }} style={{ paddingLeft: '12px', fontSize: '13px', padding: '6px 0 6px 12px' }}>
                Quick Answer Tool
              </MobileNavLink>
              <MobileNavLink to="#" onClick={(e) => {
                e.preventDefault();
                setIsDeepDiveModalOpen(true);
                closeMobileMenu();
              }} style={{ paddingLeft: '12px', fontSize: '13px', padding: '6px 0 6px 12px' }}>
                Deep Dive Analysis
              </MobileNavLink>
            </div>
            
            <div style={{ marginTop: '8px', marginBottom: '8px' }}>
              <div style={{ color: '#87CEEB', fontSize: '11px', fontWeight: '700', marginBottom: '6px', paddingLeft: '0px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Contact
              </div>
              <MobileNavLink to="/contact" onClick={closeMobileMenu} style={{ paddingLeft: '12px', fontSize: '13px', padding: '6px 0 6px 12px' }}>
                Contact Page
              </MobileNavLink>
              <div style={{ marginTop: '6px', marginBottom: '6px' }}>
                <div style={{ 
                  color: '#ffd700', 
                  fontSize: '11px', 
                  fontWeight: '600', 
                  marginBottom: '4px', 
                  paddingLeft: '12px', 
                  textTransform: 'uppercase', 
                  letterSpacing: '0.5px' 
                }}>
                  Free Consultation
                </div>
                <MobileNavLink to="/schedule" onClick={closeMobileMenu} style={{ paddingLeft: '24px', fontSize: '12px', padding: '6px 0 6px 24px' }}>
                  For Annuity Owners
                </MobileNavLink>
                <MobileNavLink to="/schedule" onClick={closeMobileMenu} style={{ paddingLeft: '24px', fontSize: '12px', padding: '6px 0 6px 24px' }}>
                  For Advisors
                </MobileNavLink>
              </div>
            </div>
            
            <div style={{ marginTop: 'auto', paddingTop: '15px', borderTop: '2px solid #87CEEB' }}>
              <a href="tel:800-922-1894" style={{
                color: '#333',
                textDecoration: 'none',
                fontSize: '16px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '10px',
                background: '#f8f8f8',
                borderRadius: '4px',
                marginBottom: '10px'
              }}>
                📞 800-922-1894
              </a>
              
              <button 
                onClick={() => {
                  openCalendly();
                  closeMobileMenu();
                }}
                style={{
                  background: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)',
                  color: '#232B3B',
                  border: 'none',
                  padding: '12px',
                  width: '100%',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                Schedule Consultation
              </button>
            </div>
          </MobileNavContainer>
      </MobileMenu>

      <MainContent>
        <Breadcrumb />
        {children}
      </MainContent>
      
      <MobileFixedFooter $isVisible={showStickyFooter}>
        <TalkWithCapButton onClick={() => setIsPhoneModalOpen(true)}>
          <span className="icon">📞</span>
          <span>TALK WITH CAP</span>
        </TalkWithCapButton>
        <FooterMenuButton onClick={(e) => toggleMobileMenu(e)}>
          <HamburgerIcon $isOpen={isMobileMenuOpen}>
            <span></span>
            <span></span>
            <span></span>
          </HamburgerIcon>
        </FooterMenuButton>
      </MobileFixedFooter>

      <Footer>
        <FooterContainer>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <FooterLogo src="/logo.svg" alt="Cap Averill II & Associates" />
            <span style={{ color: '#ffed4a', fontSize: '14px', fontWeight: '600', fontStyle: 'italic', letterSpacing: '1px', marginTop: '8px' }}>"Keep it real"</span>
          </div>
          
          <FooterCenter>
            <FooterDescription>
              Annuityanswers.com is run by Cap Averill II & Associates. We are not here to tell you what to do with your money.
              40 years in the annuity business. No fees, no obligations.
            </FooterDescription>
            <FooterTagline>
              10,000+ Clients • Zero Complaints • <strong>Underpromise & Overdeliver</strong>
              <br />
              <em style={{ fontSize: '14px', marginTop: '5px', display: 'inline-block' }}>
                "Happy clients tell the story"
              </em>
            </FooterTagline>
          </FooterCenter>
          
          <FooterBBBContainer>
            <FooterBBBLink 
              href="https://www.bbb.org/us/oh/toledo/profile/retirement-planning-services/cap-averill-ii-associates-inc-0422-13000009"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FooterBBB src="/bbb.png" alt="BBB A+ Rating" />
            </FooterBBBLink>
          </FooterBBBContainer>
        </FooterContainer>
        
        {/* Original footer nav - commented out
        <FooterLinksSection>
          <FooterLinksGrid>
            <FooterColumn>
              <FooterColumnTitle>Company</FooterColumnTitle>
              <FooterLink to="/about-us">About Us</FooterLink>
              <FooterLink to="/contact">Contact Us</FooterLink>
              <PhoneNumber href="tel:800-922-1894" style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>800-922-1894</PhoneNumber>
            </FooterColumn>
            
            <FooterColumn>
              <FooterColumnTitle>Learn</FooterColumnTitle>
              <FooterLink to="/annuity-basics">Annuity Basics</FooterLink>
              <FooterLink to="/averill-method">The Averill Method</FooterLink>
              <FooterLink to="/averill-method#date-lottery">Date Lottery Explained</FooterLink>
              <FooterLink to="/averill-method#managed-volatility">Managed Volatility Warning</FooterLink>
            </FooterColumn>
            
            <FooterColumn>
              <FooterColumnTitle>Tools</FooterColumnTitle>
            </FooterColumn>
            
            <FooterColumn>
              <FooterColumnTitle>Legal</FooterColumnTitle>
              <FooterLink to="/privacy-policy">Privacy Policy</FooterLink>
              <FooterLink to="/terms">Terms of Service</FooterLink>
              <FooterLink to="/disclosures">Disclosures</FooterLink>
            </FooterColumn>
          </FooterLinksGrid>
        </FooterLinksSection>
        */}
        
        <Copyright style={{ textAlign: 'center', padding: '30px 20px' }}>
          <div>© {new Date().getFullYear()} Cap Averill II & Associates All Rights Reserved.</div>
          <div style={{ marginTop: '10px', fontSize: '14px' }}>
            <FooterLink to="/privacy-policy" style={{ display: 'inline', margin: '0 10px' }}>Privacy</FooterLink>
            |
            <FooterLink to="/terms" style={{ display: 'inline', margin: '0 10px' }}>Terms</FooterLink>
            |
            <FooterLink to="/disclosures" style={{ display: 'inline', margin: '0 10px' }}>Disclosures</FooterLink>
          </div>
          <div style={{ 
            marginTop: '25px', 
            fontSize: '15px', 
            color: '#b0b0b0', 
            lineHeight: '1.6',
            padding: '20px',
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '8px',
            maxWidth: '900px',
            margin: '25px auto 0',
            fontWeight: '400'
          }}>
            This website is not intended as investment advice or a recommendation to purchase any specific product. 
            Always consult with qualified financial and legal advisors before making investment decisions.
          </div>
        </Copyright>
      </Footer>
      
      <DeepDiveModal
        isOpen={isDeepDiveModalOpen}
        onClose={() => setIsDeepDiveModalOpen(false)}
      />
      
      <QuickAnswerModal
        isOpen={isQuickAnswerModalOpen}
        onClose={() => setIsQuickAnswerModalOpen(false)}
      />
      
      <PhoneModal
        isOpen={isPhoneModalOpen}
        onClose={() => setIsPhoneModalOpen(false)}
        openCalendly={openCalendly}
      />
    </PageWrapper>
  );
};